import type { SupabaseClient } from '@supabase/supabase-js';
import { showToast, KF_LOGO_GOECOMMERCE, KF_LOGO_KAUFLAND } from '@vario-connect/frontend-design';
import { awaitJob, type JobProgress } from '../../lib/await-job.js';
import {
  buildAssignCsv,
  buildAssignErrorCsv,
  parseAssignCsv,
  type AssignCsvRow,
  type ParsedAssignRow,
} from '../../lib/assign-csv.js';

/**
 * Karte „Artikel-Zuordnung" — eigener App-Detail-Tab (`system.apps.{appId}.tabs` → /#/assign).
 *
 * Zwei Hälften, die zusammen einen Kreis bilden: Export der Angebote in eine CSV, in Excel
 * die Spalte „Vario-Artikelnummer" ausfüllen, Datei wieder importieren. Ersetzt die frühere
 * Bulk-Aktion „Vario zuordnen" in der Angebotsliste, die nur über EAN/SKU matchen konnte und
 * damit genau dort versagte, wo der Kunde in Vario andere Nummern führt.
 *
 * Die Einzelzuordnung im Angebots-Dialog bleibt davon unberührt.
 *
 * Keine Browser-Dialoge: die Vorschau IST die Bestätigung, und der Start-Knopf nennt die Zahl.
 */

const PAGE_SIZE = 1000;
/** Obergrenze der RPC. Größere Dateien gehen in mehreren Aufrufen raus. */
const RPC_CHUNK = 5000;

// Icons stehen bewusst VOR dem Renderer: als const am Dateiende gelesen, hätte ein früher
// Aufruf eine TDZ-Ausnahme geworfen, die weder tsc noch der Build zeigen.
const ICON_LINK =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M10 13a5 5 0 0 0 7.5.5l3-3a5 5 0 0 0-7-7l-1.7 1.7"/><path d="M14 11a5 5 0 0 0-7.5-.5l-3 3a5 5 0 0 0 7 7l1.7-1.7"/></svg>';
const ICON_DOWN =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M7 10l5 5 5-5"/><path d="M12 15V3"/></svg>';
const ICON_UP =
  '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="16" height="16"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><path d="M17 8l-5-5-5 5"/><path d="M12 3v12"/></svg>';
const ICON_SPIN = '<span class="v-icon fa-regular fa-spinner-third fa-spin"></span>';

export interface AssignIoCtx {
  supabase: SupabaseClient;
  session: { token: string; tenantId: string };
}

export interface AssignIoStorefront {
  code: string;
  label: string;
}

/** Zeile der Angebots-View, soweit für Export und Fehlerdatei gebraucht. */
interface OfferRow {
  storefront: string;
  id_offer: string | null;
  ean: string | null;
  title: string | null;
  vario_article_number: string | null;
  vario_article_id: string | null;
}

/** Einstufung einer Zeile durch die RPC. `queued` taucht in `rows` nicht auf. */
type RowStatus =
  | 'queued'
  | 'already_mapped'
  | 'unknown'
  | 'ambiguous'
  | 'no_target'
  | 'duplicate'
  | 'bad_storefront';

interface ImportRpcResult {
  queued: number;
  already_mapped: number;
  unknown: number;
  ambiguous: number;
  no_target: number;
  duplicate: number;
  bad_storefront: number;
  jobs: Array<{ job_id: string; units_count: number }>;
  rows: Array<{ i: number; status: RowStatus }>;
}

/** Klartext je Einstufung — einmal für die Vorschau, einmal für die Fehlerspalte der CSV. */
const STATUS_TEXT: Record<Exclude<RowStatus, 'queued'>, string> = {
  already_mapped: 'bereits zugeordnet',
  unknown: 'kein Angebot mit dieser SKU/EAN',
  ambiguous: 'EAN trifft mehrere Angebote',
  no_target: 'keine Vario-Artikelnummer eingetragen',
  duplicate: 'Angebot mehrfach in der Datei',
  bad_storefront: 'Storefront nicht eingerichtet',
};

function esc(s: string | null | undefined): string {
  if (s == null) return '';
  return String(s)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;');
}

function download(filename: string, content: string): void {
  // text/csv mit charset=utf-8; der BOM im Inhalt sorgt dafür, dass Excel es auch glaubt.
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Erst freigeben, wenn der Download angestoßen ist.
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

function today(): string {
  const d = new Date();
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

/** Lädt alle Angebote einer Storefront seitenweise. */
async function loadOffers(
  ctx: AssignIoCtx,
  storefront: string,
  onlyUnmapped: boolean,
  onPage: (loaded: number) => void,
): Promise<OfferRow[]> {
  const out: OfferRow[] = [];
  for (let from = 0; ; from += PAGE_SIZE) {
    let q = ctx.supabase
      .schema('kaufland')
      .from('units_with_mapping')
      .select('storefront,id_offer,ean,title,vario_article_number,vario_article_id')
      .eq('storefront', storefront)
      .is('deleted_at', null)
      .order('id_offer', { ascending: true })
      .range(from, from + PAGE_SIZE - 1);
    if (onlyUnmapped) q = q.is('vario_article_id', null);

    const { data, error } = await q;
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as OfferRow[];
    out.push(...rows);
    onPage(out.length);
    if (rows.length < PAGE_SIZE) break;
  }
  return out;
}

function offerToCsvRow(r: OfferRow): AssignCsvRow {
  return {
    sku: r.id_offer ?? '',
    varioSkuCurrent: r.vario_article_number ?? '',
    ean: r.ean ?? '',
    title: r.title ?? '',
    storefront: r.storefront,
    varioNumber: '',
  };
}

export function renderArticleAssignIo(
  host: HTMLElement,
  ctx: AssignIoCtx,
  storefronts: ReadonlyArray<AssignIoStorefront>,
): void {
  if (storefronts.length === 0) {
    host.innerHTML = '';
    return;
  }

  const sfOptions = storefronts
    .map((s) => `<option value="${esc(s.code)}">${esc(s.label)} (${esc(s.code)})</option>`)
    .join('');

  host.innerHTML = `
    <article class="mp-card kl-assign-io">
      <div class="mp-head">
        <span class="mp-code">CSV</span>
        <span class="mp-name">Artikel-Zuordnung</span>
      </div>

      <div class="mp-sec">
        <p class="kl-aio-intro">
          Angebote als Tabelle herunterladen, dort in der letzten Spalte die Vario-Artikelnummer
          eintragen, Datei wieder hochladen. Bereits zugeordnete Angebote bleiben unangetastet.
        </p>

        <div class="mp-field">
          <span class="mp-flab">Storefront</span>
          <div class="mp-select">
            <select id="kl-aio-sf">${sfOptions}</select>
          </div>
        </div>

        <label class="mp-check">
          <input type="checkbox" id="kl-aio-unmapped" checked>
          <span>Nur Angebote ohne Zuordnung exportieren</span>
        </label>

        <div class="mp-action">
          <button type="button" class="mp-btn" id="kl-aio-export">${ICON_DOWN}Tabelle exportieren</button>
          <button type="button" class="mp-btn" id="kl-aio-pick">${ICON_UP}Datei auswählen …</button>
          <input type="file" id="kl-aio-file" accept=".csv,text/csv" hidden>
          <span class="mp-msg" id="kl-aio-filename"></span>
        </div>

        <div id="kl-aio-status" class="mp-note" hidden></div>
        <div id="kl-aio-preview" hidden></div>
      </div>
    </article>`;

  const sfEl = host.querySelector<HTMLSelectElement>('#kl-aio-sf')!;
  const unmappedEl = host.querySelector<HTMLInputElement>('#kl-aio-unmapped')!;
  const exportBtn = host.querySelector<HTMLButtonElement>('#kl-aio-export')!;
  const pickBtn = host.querySelector<HTMLButtonElement>('#kl-aio-pick')!;
  const fileEl = host.querySelector<HTMLInputElement>('#kl-aio-file')!;
  const fileNameEl = host.querySelector<HTMLElement>('#kl-aio-filename')!;
  const statusEl = host.querySelector<HTMLElement>('#kl-aio-status')!;
  const previewEl = host.querySelector<HTMLElement>('#kl-aio-preview')!;

  const setStatus = (html: string | null): void => {
    statusEl.hidden = html == null;
    statusEl.innerHTML = html ?? '';
  };
  const setPreview = (html: string | null): void => {
    previewEl.hidden = html == null;
    previewEl.innerHTML = html ?? '';
  };

  // ── Export ───────────────────────────────────────────────────────────────
  exportBtn.addEventListener('click', () => {
    void (async () => {
      const storefront = sfEl.value;
      const onlyUnmapped = unmappedEl.checked;
      exportBtn.disabled = true;
      const label = exportBtn.innerHTML;
      exportBtn.innerHTML = `${ICON_SPIN}Lade …`;
      setPreview(null);
      try {
        const offers = await loadOffers(ctx, storefront, onlyUnmapped, (n) => {
          exportBtn.innerHTML = `${ICON_SPIN}${n.toLocaleString('de-DE')} geladen …`;
        });
        if (offers.length === 0) {
          setStatus(
            onlyUnmapped
              ? 'Keine Angebote ohne Zuordnung — es gibt nichts zu exportieren.'
              : 'Für diese Storefront sind keine Angebote vorhanden.',
          );
          return;
        }
        download(
          `kaufland-zuordnung-${storefront}-${today()}.csv`,
          buildAssignCsv(offers.map(offerToCsvRow)),
        );
        setStatus(
          `${offers.length.toLocaleString('de-DE')} Angebote exportiert. Trage in der Spalte „Vario-Artikelnummer" die Zielnummer ein und lade die Datei anschließend wieder hoch.`,
        );
      } catch (e) {
        showToast(`Export fehlgeschlagen: ${(e as Error).message}`, 'error');
      } finally {
        exportBtn.disabled = false;
        exportBtn.innerHTML = label;
      }
    })();
  });

  // ── Import ───────────────────────────────────────────────────────────────
  pickBtn.addEventListener('click', () => fileEl.click());

  fileEl.addEventListener('change', () => {
    const file = fileEl.files?.[0];
    // Damit dieselbe Datei nach einer Korrektur erneut gewählt werden kann.
    fileEl.value = '';
    if (!file) return;
    fileNameEl.textContent = file.name;
    void handleFile(file);
  });

  async function handleFile(file: File): Promise<void> {
    setStatus(`${ICON_SPIN}Datei wird gelesen …`);
    setPreview(null);
    let text: string;
    try {
      text = await file.text();
    } catch (e) {
      setStatus(null);
      showToast(`Datei nicht lesbar: ${(e as Error).message}`, 'error');
      return;
    }

    const parsed = parseAssignCsv(text);
    if (!parsed.ok) {
      setStatus(null);
      setPreview(`<div class="install-status install-status--error">${esc(parsed.error)}</div>`);
      return;
    }

    // Zeilen ohne eigene Storefront-Angabe erben die Auswahl aus dem Feld darüber.
    const fallbackSf = sfEl.value;
    const rows = parsed.rows.map((r) => ({ ...r, storefront: r.storefront || fallbackSf }));

    setStatus(`${ICON_SPIN}${rows.length.toLocaleString('de-DE')} Zeilen werden geprüft …`);
    let preview: ImportRpcResult;
    try {
      preview = await callRpc(rows, true);
    } catch (e) {
      setStatus(null);
      showToast(`Prüfung fehlgeschlagen: ${(e as Error).message}`, 'error');
      return;
    }
    setStatus(null);
    renderPreview(rows, preview);
  }

  /** Ruft die RPC für alle Zeilen auf und führt die Teilergebnisse zusammen. */
  async function callRpc(
    rows: ReadonlyArray<ParsedAssignRow>,
    dryRun: boolean,
  ): Promise<ImportRpcResult> {
    const total: ImportRpcResult = {
      queued: 0, already_mapped: 0, unknown: 0, ambiguous: 0,
      no_target: 0, duplicate: 0, bad_storefront: 0, jobs: [], rows: [],
    };
    for (let offset = 0; offset < rows.length; offset += RPC_CHUNK) {
      const slice = rows.slice(offset, offset + RPC_CHUNK);
      const { data, error } = await ctx.supabase.rpc('kaufland_enqueue_import_assign', {
        _rows: slice.map((r) => ({
          storefront: r.storefront,
          sku: r.sku,
          ean: r.ean,
          varioNumber: r.varioNumber,
        })),
        _dry_run: dryRun,
      });
      if (error) throw new Error(error.message);
      const part = data as ImportRpcResult;
      total.queued += part.queued;
      total.already_mapped += part.already_mapped;
      total.unknown += part.unknown;
      total.ambiguous += part.ambiguous;
      total.no_target += part.no_target;
      total.duplicate += part.duplicate;
      total.bad_storefront += part.bad_storefront;
      total.jobs.push(...part.jobs);
      // `i` ist chunk-lokal → auf die Position in der Gesamtdatei zurückrechnen.
      total.rows.push(...part.rows.map((x) => ({ ...x, i: x.i + offset })));
    }
    return total;
  }

  function renderPreview(rows: ReadonlyArray<ParsedAssignRow>, res: ImportRpcResult): void {
    const problems: Array<[Exclude<RowStatus, 'queued'>, number]> = [
      ['already_mapped', res.already_mapped],
      ['unknown', res.unknown],
      ['ambiguous', res.ambiguous],
      ['no_target', res.no_target],
      ['duplicate', res.duplicate],
      ['bad_storefront', res.bad_storefront],
    ];
    const problemHtml = problems
      .filter(([, n]) => n > 0)
      .map(
        ([k, n]) =>
          `<li><b>${n.toLocaleString('de-DE')}</b> ${esc(STATUS_TEXT[k])}</li>`,
      )
      .join('');

    setPreview(`
      <div class="kl-aio-preview">
        <div><b>${rows.length.toLocaleString('de-DE')}</b> Zeilen gelesen.</div>
        <div><b>${res.queued.toLocaleString('de-DE')}</b> Angebote werden zugeordnet.</div>
        ${problemHtml ? `<ul>${problemHtml}</ul>` : ''}
        <div class="mp-action">
          <button type="button" class="mp-btn" id="kl-aio-start" ${res.queued === 0 ? 'disabled' : ''}>
            ${ICON_LINK}${res.queued.toLocaleString('de-DE')} Zuordnungen starten
          </button>
          ${res.rows.length > 0
            ? `<button type="button" class="mp-btn" id="kl-aio-errors">${ICON_DOWN}Nicht verarbeitete Zeilen herunterladen</button>`
            : ''}
        </div>
      </div>`);

    previewEl.querySelector<HTMLButtonElement>('#kl-aio-errors')?.addEventListener('click', () => {
      downloadErrorFile(rows, res);
    });
    previewEl.querySelector<HTMLButtonElement>('#kl-aio-start')?.addEventListener('click', () => {
      void start(rows);
    });
  }

  function downloadErrorFile(
    rows: ReadonlyArray<ParsedAssignRow>,
    res: ImportRpcResult,
  ): void {
    const byIndex = new Map(res.rows.map((r) => [r.i, r.status]));
    const out = rows
      .filter((r) => byIndex.has(r.index))
      .map((r) => ({
        sku: r.sku,
        varioSkuCurrent: '',
        ean: r.ean,
        title: '',
        storefront: r.storefront,
        varioNumber: r.varioNumber,
        error: STATUS_TEXT[byIndex.get(r.index) as Exclude<RowStatus, 'queued'>] ?? 'unbekannt',
      }));
    download(`kaufland-zuordnung-offen-${today()}.csv`, buildAssignErrorCsv(out));
  }

  async function start(rows: ReadonlyArray<ParsedAssignRow>): Promise<void> {
    const startBtn = previewEl.querySelector<HTMLButtonElement>('#kl-aio-start');
    if (startBtn) {
      startBtn.disabled = true;
      startBtn.innerHTML = `${ICON_SPIN}Wird eingereiht …`;
    }
    let res: ImportRpcResult;
    try {
      res = await callRpc(rows, false);
    } catch (e) {
      if (startBtn) startBtn.disabled = false;
      showToast(`Start fehlgeschlagen: ${(e as Error).message}`, 'error');
      return;
    }

    const totalUnits = res.jobs.reduce((s, j) => s + j.units_count, 0);
    if (res.jobs.length === 0) {
      setPreview('<div class="install-status install-status--info">Es blieb nichts zuzuordnen.</div>');
      return;
    }

    // Fortschritt: der Worker schreibt alle drei Units einen Zwischenstand nach
    // account.jobs.progress. Wir summieren über alle Jobs und zeigen den Stand am Knopf.
    const perJob = new Map<string, JobProgress>();
    const recompute = (): number => {
      let processed = 0;
      for (const p of perJob.values()) processed += p.processed ?? 0;
      return processed;
    };
    const paint = (): void => {
      if (!startBtn) return;
      startBtn.innerHTML = `${ICON_SPIN}${recompute().toLocaleString('de-DE')} von ${totalUnits.toLocaleString('de-DE')} zugeordnet …`;
    };
    paint();

    const results = await Promise.all(
      res.jobs.map((j) =>
        awaitJob(ctx.supabase, j.job_id, {
          // Ein 10er-Chunk braucht bis zu einer Minute; bei vielen Chunks warten die
          // hinteren im Worker-Slot-Pool. 20 Minuten sind großzügig, aber nicht endlos.
          timeoutMs: 20 * 60_000,
          pollIntervalMs: 1500,
          onProgress: (job) => {
            if (job.progress) perJob.set(job.id, job.progress);
            paint();
          },
        })
          .then((job) => {
            if (job.progress) perJob.set(job.id, job.progress);
            return job;
          })
          .catch(() => null),
      ),
    );

    const failed = results.filter((r) => r == null || r.status !== 'completed').length;
    let matched = 0;
    let unmatched = 0;
    for (const r of results) {
      const result = r?.result as { matched?: number; unmatched?: number } | null | undefined;
      matched += result?.matched ?? 0;
      unmatched += result?.unmatched ?? 0;
    }

    const lines = [`<b>${matched.toLocaleString('de-DE')}</b> Angebote zugeordnet.`];
    if (unmatched > 0) {
      lines.push(
        `<b>${unmatched.toLocaleString('de-DE')}</b> ohne Treffer — die eingetragene Vario-Artikelnummer gibt es dort nicht.`,
      );
    }
    if (failed > 0) {
      lines.push(
        `<b>${failed}</b> von ${res.jobs.length} Teilaufträgen sind nicht durchgelaufen. Der Log-Tab zeigt den Grund.`,
      );
    }
    setPreview(
      `<div class="install-status ${failed > 0 ? 'install-status--error' : 'install-status--ok'}">${lines.join('<br>')}</div>`,
    );
    showToast(
      failed > 0 ? 'Zuordnung mit Fehlern beendet.' : `${matched.toLocaleString('de-DE')} Angebote zugeordnet.`,
      failed > 0 ? 'error' : 'success',
    );
  }
}

// ---------------------------------------------------------------------------
// Standalone-Slot (`/#/assign`) — eigener App-Detail-Tab in Vario
// ---------------------------------------------------------------------------

/** Anzeigenamen der Storefronts. Die RPC liefert nur die Codes. */
const STOREFRONT_LABELS: Record<string, string> = {
  de: 'Deutschland', at: 'Österreich', cz: 'Tschechien',
  sk: 'Slowakei', pl: 'Polen', fr: 'Frankreich', it: 'Italien', hr: 'Kroatien',
};

interface AssignSlotCtx {
  contentEl: HTMLElement;
  supabase: SupabaseClient;
  session: { token: string; tenantId: string; marketplaceKind?: string };
  supabaseUrl: string;
}

/**
 * Slot-Renderer für den Vario-App-Tab `/#/assign`. Rahmen wie bei den Einstellungen:
 * kf-page über die volle iframe-Höhe, eigener Kopf, Inhalt scrollt innen.
 *
 * Lädt die freigeschalteten Storefronts selbst (`kaufland_list_storefronts`) — anders als in
 * den Einstellungen, wo sie ohnehin schon vom Status-Endpoint kommen.
 */
export async function renderArticleAssignSlot(ctx: AssignSlotCtx): Promise<void> {
  ctx.contentEl.hidden = false;
  ctx.contentEl.innerHTML = `
    <div class="kf-page" style="height:100%;display:flex;flex-direction:column;">
      <main class="main">
        <header class="apphead">
          <a class="brand" href="https://www.go-ecommerce.de" target="_blank" rel="noopener" title="go eCommerce">${KF_LOGO_GOECOMMERCE}</a>
          <span class="kf-logo" title="Kaufland.de">${KF_LOGO_KAUFLAND}</span>
          <span class="hdivider"></span>
          <div class="htitle"><div class="heyebrow">Kaufland · Artikel-Zuordnung</div><h1>Angebote mit Vario-Artikeln verknüpfen</h1></div>
          <div class="hactions"><span class="hstatus"><span class="d"></span>Kaufland verbunden</span></div>
        </header>
        <div class="scrollarea">
          <div id="kl-assign-io" class="kl-assign-io-wrap">Lade Storefronts …</div>
        </div>
      </main>
    </div>`;

  // Volle iframe-Höhe ausnutzen; Scrolling passiert innen (gleiche Mechanik wie Einstellungen).
  const root = ctx.contentEl.querySelector<HTMLElement>('.kf-page')!;
  const fit = () => { root.style.height = `${window.innerHeight}px`; };
  window.addEventListener('resize', fit);
  window.addEventListener('orientationchange', fit);
  fit();

  const hostEl = ctx.contentEl.querySelector<HTMLDivElement>('#kl-assign-io')!;
  const { data, error } = await ctx.supabase.rpc('kaufland_list_storefronts');
  if (error) {
    hostEl.innerHTML = `<div class="install-status install-status--error">Storefronts nicht ladbar: ${esc(error.message)}</div>`;
    return;
  }
  const storefronts = ((data ?? []) as Array<{ storefront: string }>).map((r) => ({
    code: r.storefront,
    label: STOREFRONT_LABELS[r.storefront] ?? r.storefront.toUpperCase(),
  }));
  if (storefronts.length === 0) {
    hostEl.innerHTML =
      '<div class="install-status install-status--info">Noch keine Storefront eingerichtet. Lege sie zuerst in den Marktplatz-Einstellungen an.</div>';
    return;
  }
  renderArticleAssignIo(hostEl, { supabase: ctx.supabase, session: ctx.session }, storefronts);
}
